import {
  BrainCircuit,
  MessagesSquare,
  FileSignature,
  Wrench,
  ClipboardList,
  Boxes,
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  Kanban,
  Settings,
  DollarSign,
  Building2,
  ShieldCheck,
  Shield,
  Network,
  BarChart3,
  TrendingUp,
  Layers,
  Lightbulb,
  ScrollText,
  Package,
  Tag,
  Calendar,
  GitBranch,
  Users,
  UserCog,
  Key,
  Target,
  Rocket,
  Wallet,
  Handshake,
  Contact,
  FileText,
  Inbox,
  Receipt,
  Hash,
  Cpu,
  Activity,
  Palette,
  type LucideIcon,
} from 'lucide-react';
import type { PanelType } from '../../types';

/**
 * Two-level navigation: a narrow primary rail of groups, and a secondary rail
 * that opens alongside it listing that group's destinations.
 *
 * `route` vs `panel` is not a style choice. A **panel** is a PICKER: it lists
 * things and navigates the main area when you choose one, so the page beside it
 * is the whole point — Projects, Funding, Funders, Release Notes. A **route** is
 * a destination that stands on its own.
 *
 * Settings, Organizations, Program Increments and Tasks were panels that
 * navigated nowhere, so the main area kept whatever happened to be there:
 * opening Settings left you editing statuses beside a table of 75 team members.
 * They are routes now. Docs and API Docs stay panels deliberately — reading
 * reference material beside the work is what they are for.
 *
 * Fifteen flat icons had outgrown a tooltip-only rail, and two things were in
 * the wrong place: Program Increments were buried in Org Admin even though
 * cadence is what generates releases, and the workflow engine had no entry at
 * all. Grouping fixed both.
 */

export interface NavItem {
  id: string;
  icon: LucideIcon;
  label: string;
  /** Shown in the secondary rail under the label. */
  description?: string;
  /** Opens a slideout panel. */
  panel?: PanelType;
  /** Navigates to a route. */
  route?: string;
  /** URL prefix that also highlights this item (for panel-driven pages). */
  routeMatch?: string;
  /** Highlight on this exact path only, not its children ('/ways' but not '/ways/design-standard'). */
  exact?: boolean;
  /** Visible only to holders of one of these roles. */
  requiredRoles?: string[];
  /** Visible only where this module is switched on for one of the user's orgs. */
  requiredModule?: string;
}

export interface NavGroup {
  id: string;
  icon: LucideIcon;
  label: string;
  /** Empty for a direct destination such as Dashboard. */
  items: NavItem[];
  /** Set when the group is itself a destination rather than a flyout. */
  route?: string;
  requiredRoles?: string[];
  requiredModule?: string;
}

export const navGroups: NavGroup[] = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    route: '/',
    items: [],
  },
  {
    // Work that has not been committed to a sprint yet.
    id: 'plan',
    icon: Target,
    label: 'Plan',
    items: [
      { id: 'ideation', icon: Lightbulb, label: 'Ideation', description: 'Capture and shape ideas', route: '/ideation' },
      { id: 'portfolio', icon: Layers, label: 'Portfolio', description: 'Epics across projects', route: '/portfolio', requiredRoles: ['super_admin', 'project_admin'] },
    ],
  },
  {
    // Work in flight.
    id: 'deliver',
    icon: Rocket,
    label: 'Deliver',
    items: [
      { id: 'projects', icon: FolderKanban, label: 'Projects', description: 'Browse and open projects', panel: 'projects', routeMatch: '/project' },
      { id: 'board', icon: Kanban, label: 'Board', description: 'Kanban by workflow state', route: '/board' },
      // Moved out of Plan: a ranking answers "what do we do next", which is a
      // question about work in flight, not about work not yet committed.
      { id: 'priorities', icon: TrendingUp, label: 'Priorities', description: 'WSJF ranking within the hierarchy', route: '/priorities' },
      { id: 'tasks', icon: ListTodo, label: 'Tasks', description: 'Assigned work across projects', route: '/tasks' },
      { id: 'gantt', icon: BarChart3, label: 'Gantt', description: 'Schedule and dependencies', route: '/gantt' },
    ],
  },
  {
    // Cadence, releases and the notes they produce — one journey, one group.
    id: 'release-management',
    icon: Package,
    label: 'Release Management',
    items: [
      { id: 'releases', icon: Tag, label: 'Releases', description: 'Scope, readiness and approvals', route: '/releases' },
      { id: 'release-notes', icon: ScrollText, label: 'Release Notes', description: 'Published notes by project', panel: 'release-notes', routeMatch: '/release-notes' },
      // Moved out of Org Admin: cadence drives release generation.
      { id: 'program-increments', icon: Calendar, label: 'Program Increments', description: 'PIs, sprints and cadence', route: '/admin/program-increments', requiredRoles: ['super_admin'] },
    ],
  },
  {
    // EP-26: the Model Training module. A direct destination; the programme is
    // chosen inside the page (and kept in the URL), because every page of the
    // module is scoped to one programme. Access is enforced server-side per
    // programme grant; the roles here only decide whether the rail shows it.
    id: 'training',
    icon: BrainCircuit,
    label: 'Model Training',
    route: '/training',
    items: [],
    requiredRoles: ['super_admin', 'project_admin', 'project_resource'],
  },
  {
    // PO-92 F5.1: Ways -- channels, messages and decisions shared by people and agent
    // sessions (hs-ways-to-work). Gated on the `ways` module, which is granted per org.
    // Channels, Runners and Usage open as views in the Ways main area (`?view=`), not as
    // slide-outs: the channels slide-out repeated the list already in the Ways navigation
    // (PO46-1649, Zane 2026-10-27). Old `?panel=` links are rewritten by the page. The Design
    // Standard has its own page.
    id: 'ways',
    icon: MessagesSquare,
    label: 'Ways',
    requiredModule: 'ways',
    items: [
      { id: 'ways-messages', icon: MessagesSquare, label: 'Messages', description: 'Your sidebar and channels', route: '/ways', exact: true },
      { id: 'ways-channels', icon: Hash, label: 'Channels', description: 'Every channel you can read', route: '/ways?view=channels' },
      { id: 'ways-runners', icon: Cpu, label: 'Runners', description: 'Live runner sessions', route: '/ways?view=runners' },
      { id: 'ways-usage', icon: Activity, label: 'Usage', description: 'Tokens by project and session', route: '/ways?view=usage' },
      { id: 'ways-design-standard', icon: Palette, label: 'Design Standard', description: 'View, versions, owner edits', route: '/ways/design-standard' },
      // PO-92 EP-10 (Zane, 2026-10-29: "in a ways rail slideout below design standard"): tools by sourcing
      // tier, standards, and what the repository scan recommends per project.
      { id: 'ways-technology', icon: Layers, label: 'Technology', description: 'Tools, standards and recommendations', route: '/ways?view=technology' },
    ],
  },
  {
    // EP-26 FE-7: every approval request this person may decide, across modules,
    // and their own open requests. A direct destination for everyone signed in:
    // WHO may decide is answered per request by the server, never by the rail.
    id: 'approvals',
    icon: Inbox,
    label: 'Approvals',
    route: '/approvals',
    items: [],
  },
  {
    // Operations, not delivery. Its own rail entry rather than a child of a
    // delivery group: the whole point of the ITSM board is that an Incident is
    // not a story, and burying it under delivery would say the opposite.
    id: 'itsm',
    icon: Wrench,
    label: 'Operations',
    // The six ITIL types are still switched INSIDE the board, where their counts are
    // visible; splitting them into rail entries would hide how much is open behind a
    // click each. The secondary rail holds the two views of the work instead: every
    // ticket, and mine (EP-27 FE-4, PO46-1288: open tickets in the queues -- user
    // groups -- I belong to). A flyout rather than a 13th rail domain (nav-rail).
    items: [
      { id: 'itsm-board', icon: Wrench, label: 'Service desk', description: 'Every ticket, by ITIL type', route: '/itsm' },
      { id: 'my-queue', icon: ClipboardList, label: 'My queue', description: 'Open tickets in my queues, by SLA', route: '/my-queue' },
    ],
    requiredRoles: ['super_admin', 'project_admin', 'project_resource'],
  },
  {
    // Beside Operations rather than inside it: the CMDB is the asset register
    // the whole platform hangs off — the graph's asset layer — and burying it
    // under a ticketing board would read as "assets exist because tickets do".
    id: 'cmdb',
    icon: Boxes,
    label: 'CMDB',
    route: '/cmdb',
    items: [],
    requiredRoles: ['super_admin', 'project_admin', 'project_resource'],
  },
  {
    // Commercial pipeline. Separate from Funding, which is grant and award work
    // — different lifecycle, different people, different money.
    id: 'crm',
    icon: Handshake,
    label: 'CRM',
    requiredRoles: ['super_admin', 'project_admin'],
    items: [
      { id: 'leads', icon: Contact, label: 'Leads', description: 'Inbound enquiries and their pipeline', route: '/crm/leads', requiredRoles: ['super_admin', 'project_admin'] },
      { id: 'opportunities', icon: Target, label: 'Opportunities', description: 'Commercial pipeline and forecast', route: '/crm/opportunities', requiredRoles: ['super_admin', 'project_admin'] },
      // Not "Clients": the same page administers resellers and groups, and a
      // partner IS an account typed `reseller`. Labelling it Clients is why
      // nobody could find where a partner is created.
      { id: 'clients', icon: Building2, label: 'Organisations', description: 'Clients, resellers and groups', route: '/crm/clients', requiredRoles: ['super_admin', 'project_admin'] },
      { id: 'catalog', icon: Package, label: 'Catalog & pricing', description: 'Products, regions and discount rules', route: '/crm/catalog', requiredRoles: ['super_admin', 'project_admin'] },
      { id: 'quotes', icon: FileText, label: 'Quotes', description: 'Pricing, discounts and approvals', route: '/crm/quotes', requiredRoles: ['super_admin', 'project_admin'] },
      { id: 'invoices', icon: Receipt, label: 'Invoices', description: 'Billed from accepted quotes', route: '/crm/invoices', requiredRoles: ['super_admin', 'project_admin'] },
      // ONE place for contracts — templates and signed agreements together.
      // Contract capability spread across Organisations and Quotes would mean
      // three gates to keep in step, and the forgotten one is the one that
      // leaks. Generation still starts in context (a partner org, a quote);
      // what lives here is the listing, editing and control.
      { id: 'contracts', icon: FileSignature, label: 'Contracts', description: 'Templates, partner agreements and order forms', route: '/crm/contracts', requiredRoles: ['super_admin', 'project_admin'] },
    ],
  },
  {
    id: 'funding',
    icon: Wallet,
    label: 'Funding',
    requiredRoles: ['super_admin', 'project_admin'],
    items: [
      { id: 'funding', icon: DollarSign, label: 'Funding Pipeline', description: 'Applications and awards', panel: 'funding', routeMatch: '/funding', requiredRoles: ['super_admin', 'project_admin'] },
      { id: 'funders', icon: Building2, label: 'Funders', description: 'Funding bodies and contacts', panel: 'funders', routeMatch: '/funder', requiredRoles: ['super_admin', 'project_admin'] },
    ],
  },
  {
    // Configuration only. Anything operational lives in the groups above.
    id: 'administration',
    icon: ShieldCheck,
    label: 'Administration',
    items: [
      { id: 'org-admin', icon: Network, label: 'Organizations', description: 'Orgs, programs and release trains', route: '/admin/organizations', requiredRoles: ['super_admin'] },
      { id: 'workflows', icon: GitBranch, label: 'Workflows', description: 'States, transitions and guards', route: '/admin/workflows', requiredRoles: ['super_admin'] },
      { id: 'users', icon: UserCog, label: 'User Management', description: 'Manage user roles and access', route: '/admin/users', requiredRoles: ['super_admin'] },
      { id: 'access', icon: Shield, label: 'Access Control', description: 'Roles, groups and grants', route: '/admin/access', requiredRoles: ['super_admin'] },
      { id: 'outbox', icon: Inbox, label: 'Outbox', description: 'Delivery queue and dead letters', route: '/admin/outbox', requiredRoles: ['project_admin', 'super_admin'] },
      { id: 'api-tokens', icon: Key, label: 'API Tokens', description: 'Service account tokens', route: '/admin/api-tokens', requiredRoles: ['super_admin'] },
      { id: 'resources', icon: Users, label: 'Resources', description: 'Team members and assignees', route: '/admin/resources', requiredRoles: ['super_admin', 'project_admin'] },
      { id: 'settings', icon: Settings, label: 'Settings', description: 'Statuses, types and defaults', route: '/settings', requiredRoles: ['super_admin'] },
    ],
  },
];

/** Does this item correspond to the current location? */
export function itemMatchesPath(item: NavItem, pathname: string): boolean {
  if (item.route === '/') return pathname === '/';
  // A route carrying a query (a view inside a page) is not matched on the pathname alone.
  if (item.route?.includes('?')) return false;
  if (item.route && item.exact) return pathname === item.route;
  if (item.route && pathname.startsWith(item.route)) return true;
  if (item.routeMatch && pathname.startsWith(item.routeMatch)) return true;
  return false;
}

/** The group owning a pathname, so the rails can follow a deep link. */
export function groupForPath(pathname: string): NavGroup | undefined {
  // Most specific wins: '/admin/users' must not be claimed by a '/' route.
  const scored = navGroups
    .flatMap((group) =>
      group.items
        .filter((item) => itemMatchesPath(item, pathname))
        .map((item) => ({ group, weight: (item.route || item.routeMatch || '').length }))
    )
    .sort((a, b) => b.weight - a.weight);
  return scored[0]?.group;
}
